/**
 * Utility functions for CSV column handling and display
 */

import {
  APPFOLIO_REQUIRED_FIELDS,
  APPFOLIO_COMMON_FIELDS,
} from '../transformers/appfolio-transformer'
import { APPFOLIO_PROPERTY_REQUIRED_FIELDS } from '../transformers/appfolio-property-transformer'
import { APPFOLIO_UNIT_REQUIRED_FIELDS } from '../transformers/appfolio-unit-transformer'

export const MAX_SAMPLE_HEADERS = 4

export interface ColumnInfo {
  name: string
  isRequired: boolean
}

/**
 * Gets the required fields for a given PMS provider and upload type
 */
export type UploadType = 'contacts' | 'properties' | 'units'

export function getRequiredFields(
  pmsProvider: string,
  uploadType: UploadType = 'contacts',
): readonly string[] {
  if (uploadType === 'units') {
    switch (pmsProvider) {
      case 'appfolio':
        return APPFOLIO_UNIT_REQUIRED_FIELDS
      default:
        return ['Unit Name', 'Property']
    }
  }

  if (uploadType === 'properties') {
    switch (pmsProvider) {
      case 'appfolio':
        return APPFOLIO_PROPERTY_REQUIRED_FIELDS
      default:
        return ['Property ID', 'Property Address']
    }
  }

  // Contact fields
  switch (pmsProvider) {
    case 'appfolio':
      return APPFOLIO_REQUIRED_FIELDS
    default:
      return []
  }
}

/**
 * Gets the common/recommended fields for a given PMS provider and upload type
 */
export function getCommonFields(
  pmsProvider: string,
  uploadType: UploadType = 'contacts',
): readonly string[] {
  if (uploadType === 'units') {
    switch (pmsProvider) {
      case 'appfolio':
        return [
          'Unit Name',
          'Property',
          'Unit Street Address 1',
          'Unit Street Address 2',
        ]
      default:
        return ['Unit Name', 'Property', 'Unit Street Address 1']
    }
  }

  if (uploadType === 'properties') {
    switch (pmsProvider) {
      case 'appfolio':
        return APPFOLIO_PROPERTY_REQUIRED_FIELDS
      default:
        return [
          'Property ID',
          'Property Address',
          'Property Name',
          'Property Type',
        ]
    }
  }

  // Contact fields
  switch (pmsProvider) {
    case 'appfolio':
      return APPFOLIO_COMMON_FIELDS
    default:
      return ['First Name', 'Last Name', 'Email', 'Phone']
  }
}

/**
 * Sorts headers with required fields first (alphabetically), then other fields (alphabetically)
 */
export function sortHeaders(
  headers: string[],
  pmsProvider: string,
  uploadType: UploadType = 'contacts',
): ColumnInfo[] {
  const requiredFields = getRequiredFields(pmsProvider, uploadType)

  return headers
    .map(header => ({
      name: header,
      isRequired: requiredFields.includes(header),
    }))
    .sort((a, b) => {
      // Required fields first
      if (a.isRequired && !b.isRequired) return -1
      if (!a.isRequired && b.isRequired) return 1

      // Within each group, sort alphabetically
      return a.name.localeCompare(b.name)
    })
}

/**
 * Gets the most important headers for sample data display
 * Prioritizes required fields and common fields
 */
export function getSampleHeaders(
  headers: string[],
  pmsProvider: string,
  maxCount: number = MAX_SAMPLE_HEADERS,
  uploadType: UploadType = 'contacts',
): string[] {
  const commonFields = getCommonFields(pmsProvider, uploadType)

  // Filter headers to only include common fields that exist, in priority order
  const availableCommon = commonFields.filter(field => headers.includes(field))

  // Take up to maxCount from the common fields
  const result = availableCommon.slice(0, maxCount)

  // If we still need more headers, add other available ones
  if (result.length < maxCount) {
    const remainingHeaders = headers.filter(h => !result.includes(h))
    result.push(...remainingHeaders.slice(0, maxCount - result.length))
  }

  return result
}
